import {
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_FUND,
  PERMISSION_ACTION_VIEW,
  PERMISSION_ENTITY_INVOICE_OFFER
} from '@klear/constants'
import type { OrderEventActionFormValues } from '@klear/order-event/event-form'
import { EMPTY_ORDER_EVENT_ACTION_FORM } from '@klear/order-event/wire.client'
import type { OrderEventActionEnum, OrderEventOrderEventType, OrderEventType } from '@klear/order-event/constants'
import { Button } from '@klear/ui/components/button'
import { MoreHorizontal } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@klear/ui/components/dropdown-menu'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { useMemo, useState } from 'react'
import { useOrderEventTimelineTree } from '@klear/ui/hooks/use-order-events'
import { useEventActionConfig, type ActionConfig } from '@klear/ui/hooks/use-event-action-config'
import { orderEventRef } from '@klear/ui/hooks/order-event-ref'
import { EventActionsConfirmModal } from '@klear/order-event-ui/event-actions-confirm-modal'
import {
  availableActionsFromTimeline,
  buildTimelineActionContext,
  defaultAddEventTypeFromTimeline,
  type TimelineActionContext,
  type TimelineActionOption
} from '@klear/order-event-ui/event-actions-availability'

interface EventActionsProps {
  eventId: number
  eventType: OrderEventOrderEventType
  orderId: number
  traderId: number
  parentEventId?: number
  timelineActionContext?: TimelineActionContext
  timelineEventsLoading?: boolean
  onView?: () => void
  isViewDisabled?: boolean
  onViewOffer?: () => void
  onEdit?: () => void
  onMarkComplete?: () => void
  onMarkFunded?: () => void
  onMarkFeesPaid?: () => void
  onAdvanceRecovery?: () => void
  onFund?: () => void
  onSuccess?: (action?: OrderEventActionEnum, addEventType?: OrderEventType) => void
  canAddInvoiceOffer?: boolean
  isEditing: boolean
  label?: string
}

export function EventActions({
  eventId,
  eventType,
  orderId,
  traderId,
  parentEventId,
  timelineActionContext: timelineActionContextProp,
  timelineEventsLoading = false,
  onEdit,
  onMarkComplete,
  onMarkFunded,
  onMarkFeesPaid,
  onAdvanceRecovery,
  onFund,
  onView,
  isViewDisabled = false,
  onViewOffer,
  onSuccess,
  canAddInvoiceOffer = true,
  isEditing,
  label = 'Actions'
}: EventActionsProps) {
  const eventRef = useMemo(
    () => orderEventRef({ id: eventId, orderId, traderId, type: eventType }),
    [eventId, orderId, traderId, eventType]
  )

  const hasTimelineContext = timelineActionContextProp != null
  const { user } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedAction, setSelectedAction] = useState<OrderEventActionEnum | null>(null)
  const [timelineReady, setTimelineReady] = useState(hasTimelineContext)
  const activateTimeline = () => {
    if (!timelineReady) setTimelineReady(true)
  }
  const { events: fetchedEvents, isLoading: isFetchingTimeline } = useOrderEventTimelineTree(
    hasTimelineContext || timelineReady ? orderId : undefined
  )
  const timelineActionContext = timelineActionContextProp ?? buildTimelineActionContext(fetchedEvents)
  const isTimelineLoading = hasTimelineContext ? timelineEventsLoading : isFetchingTimeline
  const { getActionConfig } = useEventActionConfig()
  const [actionConfig, setActionConfig] = useState<ActionConfig | null>(null)
  const [actionValues, setActionValues] = useState<OrderEventActionFormValues>(
    EMPTY_ORDER_EVENT_ACTION_FORM('' as OrderEventActionEnum)
  )
  const [menuOpen, setMenuOpen] = useState(false)

  const handleAction = async (option: TimelineActionOption) => {
    if (option.viewTarget === PERMISSION_ENTITY_INVOICE_OFFER) {
      onViewOffer?.()
      return
    }
    const action = option.value
    if (onView && action === PERMISSION_ACTION_VIEW) {
      onView()
      return
    }
    if (action === PERMISSION_ACTION_EDIT) {
      onEdit?.()
      return
    }
    if (onFund && action === PERMISSION_ACTION_FUND) {
      onFund()
      return
    }
    if (action === PERMISSION_ACTION_ADD) {
      const addEventType = option.addEventType ?? defaultAddEventTypeFromTimeline(timelineActionContext, eventId)
      onSuccess?.(action, addEventType)
      return
    }

    try {
      const config = getActionConfig(eventType)
      setMenuOpen(false)
      setSelectedAction(action)
      setActionConfig(config[action] || null)
      setDialogOpen(true)
      setActionValues(EMPTY_ORDER_EVENT_ACTION_FORM(action))
    } catch (error) {
      console.error('Error processing action:', error)
    }
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    setSelectedAction(null)
    setActionConfig(null)
    setActionValues((current) => ({ ...current, notes: '' }))
  }

  const availableActions = useMemo(() => {
    if (!timelineReady || isTimelineLoading) {
      return []
    }

    return availableActionsFromTimeline(user, timelineActionContext, eventId).filter((option) => {
      if (option.value === PERMISSION_ACTION_EDIT) return onEdit != null
      if (option.addEventType === PERMISSION_ENTITY_INVOICE_OFFER) return canAddInvoiceOffer
      if (option.value !== PERMISSION_ACTION_VIEW) return true
      return option.viewTarget === PERMISSION_ENTITY_INVOICE_OFFER ? onViewOffer != null : onView != null
    })
  }, [
    canAddInvoiceOffer,
    eventId,
    isTimelineLoading,
    onEdit,
    onView,
    onViewOffer,
    timelineActionContext,
    timelineReady,
    user
  ])

  const isInvoiceOffer = eventType === PERMISSION_ENTITY_INVOICE_OFFER
  const hasInvoiceOfferActions =
    isInvoiceOffer && Boolean(onMarkComplete || onMarkFunded || onMarkFeesPaid || onAdvanceRecovery)

  if (isEditing) {
    return null
  }

  const confirmModal =
    dialogOpen && selectedAction != null && actionConfig != null ? (
      <EventActionsConfirmModal
        isOpen={dialogOpen}
        onClose={handleDialogClose}
        onSuccess={(action) => onSuccess?.(action)}
        eventRef={eventRef}
        parentEventId={parentEventId}
        selectedAction={selectedAction}
        actionConfig={actionConfig}
        actionValues={actionValues}
        onNotesChange={(notes) => setActionValues((current) => ({ ...current, notes }))}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
      />
    ) : null

  if (!timelineReady) {
    return (
      <>
        <Button
          variant="ghost"
          className="h-8 w-8 p-0"
          onClick={() => {
            activateTimeline()
            setMenuOpen(true)
          }}
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
        {confirmModal}
      </>
    )
  }

  let menuItems
  if (isTimelineLoading) {
    menuItems = <DropdownMenuItem disabled>Loading...</DropdownMenuItem>
  } else if (availableActions.length === 0 && !hasInvoiceOfferActions) {
    menuItems = <DropdownMenuItem disabled>No actions available</DropdownMenuItem>
  } else {
    menuItems = (
      <>
        {availableActions.map((actionOption, index) => (
          <DropdownMenuItem
            key={`${actionOption.viewTarget ?? actionOption.value}-${index}-${actionOption.label}`}
            disabled={
              isViewDisabled && actionOption.value === PERMISSION_ACTION_VIEW && actionOption.viewTarget == null
            }
            onClick={() => handleAction(actionOption)}
          >
            {actionOption.label}
          </DropdownMenuItem>
        ))}
        {isInvoiceOffer && onMarkFunded && (
          <DropdownMenuItem onClick={() => onMarkFunded()}>Mark funded</DropdownMenuItem>
        )}
        {isInvoiceOffer && onMarkFeesPaid && (
          <DropdownMenuItem onClick={() => onMarkFeesPaid()}>Mark fees paid</DropdownMenuItem>
        )}
        {isInvoiceOffer && onMarkComplete && (
          <DropdownMenuItem onClick={() => onMarkComplete()}>Mark complete</DropdownMenuItem>
        )}
        {isInvoiceOffer && onAdvanceRecovery && (
          <DropdownMenuItem onClick={() => onAdvanceRecovery()}>Return advance</DropdownMenuItem>
        )}
      </>
    )
  }

  return (
    <>
      <DropdownMenu
        open={menuOpen}
        onOpenChange={(open) => {
          setMenuOpen(open)
          if (open) activateTimeline()
        }}
      >
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0" aria-label={label} disabled={isLoading || isTimelineLoading}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">{menuItems}</DropdownMenuContent>
      </DropdownMenu>

      {confirmModal}
    </>
  )
}
